import { startTrace, stopTrace } from '@/lib/analytics';
import type { BibleVersion } from '@/lib/utils/bible-utils';
import { getDownloadURL, ref, type FirebaseStorage } from 'firebase/storage';

// Storage paths for each translation version
const TRANSLATION_PATHS: Record<BibleVersion, string> = {
    csb: 'csb/csb-paragraphs.json',
    esv: 'esv/esv-paragraphs.json',
    kjv: 'kjv/kjv-paragraphs.json',
    nasb: 'nasb/nasb-paragraphs.json',
    niv: 'niv/niv-paragraphs.json',
    nkjv: 'nkjv/nkjv-paragraphs.json'
};

export async function fetchTranslationsFromDB(versions: BibleVersion[], storage: FirebaseStorage): Promise<TranslationByParagraph[]> {
    const fetchPromises = versions.map(async version => {
        const translationTrace = startTrace(`fetch_translation_${version}`);
        try {
            const path = TRANSLATION_PATHS[version];
            if (!path) {
                console.warn(`No storage path configured for translation version: ${version}`);
                return null;
            }

            // Get data directly from Firebase Storage using direct public URLs for speed
            const bucket = 'bible-glen.firebasestorage.app';
            const encodedPath = encodeURIComponent(path);
            const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

            const response = await fetch(url);
            const data = await response.json();

            stopTrace(translationTrace, { version });
            return { ...data, version, id: version } as TranslationByParagraph;
        }
        catch (error) {
            stopTrace(translationTrace, { status: 'error' });
            console.error(`Error fetching translation ${version}:`, error);
            return null;
        }
    });

    // Wait for all fetches to complete and filter out failed ones
    const results = await Promise.all(fetchPromises);
    return results.filter(translation => translation !== null);
}
